// src/components/News/ArticlePage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WordPressPost, WordPressMedia, wordpressApi } from '../../services/wordpressApi';

// Composant pour charger l'image des articles liés
const RelatedPostImage: React.FC<{ mediaId: number; title: string }> = ({ mediaId, title }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadImage = async () => {
            try {
                const media = await wordpressApi.getMedia(mediaId);
                setImageUrl(media.source_url);
            } catch (error) {
                console.error('Erreur chargement image article lié:', error);
            } finally {
                setLoading(false);
            }
        };

        if (mediaId) {
            loadImage();
        } else {
            setLoading(false);
        }
    }, [mediaId]);

    if (loading) {
        return (
            <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                <span className="text-gray-400 text-sm">Chargement...</span>
            </div>
        );
    }

    if (!imageUrl) {
        return (
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <svg className="w-12 h-12 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
            </div>
        );
    }

    return (
        <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
        />
    );
};

const ArticlePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const navigate = useNavigate();
    const [post, setPost] = useState<WordPressPost | null>(null);
    const [featuredImage, setFeaturedImage] = useState<string | null>(null);
    const [relatedPosts, setRelatedPosts] = useState<WordPressPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [debugInfo, setDebugInfo] = useState<string>('');

    useEffect(() => {
        const loadArticle = async () => {
            if (!slug) {
                setError('Slug manquant dans l\'URL');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                setDebugInfo(`Recherche de l'article avec le slug: "${slug}"`);

                // Méthode 1: Recherche par slug exact
                let currentPost = await wordpressApi.getPostBySlug(slug);

                if (!currentPost) {
                    setDebugInfo(`Article non trouvé avec slug "${slug}". Tentative de recherche alternative...`);

                    // Méthode 2: Rechercher tous les articles et filtrer
                    const allPosts = await wordpressApi.getPosts({ per_page: 100 });

                    currentPost = allPosts.find(p => p.slug === slug) || null;

                    if (!currentPost) {
                        // Méthode 3: Recherche approximative (au cas où le slug aurait des différences)
                        const slugVariations = [
                            slug,
                            slug.replace(/-/g, ''),
                            slug.toLowerCase(),
                            decodeURIComponent(slug)
                        ];

                        for (const variation of slugVariations) {
                            currentPost = allPosts.find(p =>
                                p.slug === variation ||
                                p.slug.includes(variation) ||
                                variation.includes(p.slug)
                            ) || null;

                            if (currentPost) {
                                setDebugInfo(`Article trouvé avec variation: "${variation}" -> "${currentPost.slug}"`);
                                break;
                            }
                        }
                    } else {
                        setDebugInfo(`Article trouvé dans la liste complète: "${currentPost.slug}"`);
                    }
                } else {
                    setDebugInfo(`Article trouvé directement: "${currentPost.slug}"`);
                }

                if (!currentPost) {
                    setError(`Article avec le slug "${slug}" introuvable. Vérifiez l'URL.`);
                    setDebugInfo(debugInfo + ' | Aucune correspondance trouvée.');
                    return;
                }

                setPost(currentPost);

                // Charger l'image à la une
                if (currentPost.featured_media) {
                    try {
                        const media: WordPressMedia = await wordpressApi.getMedia(currentPost.featured_media);
                        setFeaturedImage(media.source_url);
                    } catch (err) {
                        console.error('Erreur chargement image:', err);
                    }
                }

                // Charger les articles liés (même catégorie)
                if (currentPost.categories.length > 0) {
                    try {
                        const related = await wordpressApi.getRelatedPosts(
                            currentPost.id,
                            currentPost.categories,
                            3
                        );
                        setRelatedPosts(related);
                    } catch (err) {
                        console.error('Erreur articles liés:', err);
                    }
                }

            } catch (err) {
                console.error('Erreur complète:', err);
                setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
                setDebugInfo(debugInfo + ` | Erreur: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
            } finally {
                setLoading(false);
            }
        };

        loadArticle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Fonction pour nettoyer et sécuriser le contenu HTML
    const createMarkup = (html: string) => {
        return { __html: html };
    };

    // Fonction pour afficher une notification toast
    const showToast = (message: string) => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300';
        document.body.appendChild(toast);

        // Animation d'entrée
        setTimeout(() => toast.classList.add('translate-x-0'), 10);

        // Suppression après 3 secondes
        setTimeout(() => {
            toast.classList.add('translate-x-full', 'opacity-0');
            setTimeout(() => {
                if (document.body.contains(toast)) {
                    document.body.removeChild(toast);
                }
            }, 300);
        }, 3000);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-16">
                <div className="max-w-4xl mx-auto px-4 py-8">
                    <div className="animate-pulse">
                        {/* Skeleton pour le titre */}
                        <div className="h-12 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg mb-6"></div>

                        {/* Skeleton pour l'image */}
                        <div className="h-64 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl mb-8 shadow-lg"></div>

                        {/* Skeleton pour le contenu */}
                        <div className="space-y-6">
                            {[1, 2, 3, 4, 5].map(i => (
                                <div key={i} className="space-y-3">
                                    <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
                                    <div className="h-4 bg-gray-200 rounded"></div>
                                    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Info de debug avec style amélioré */}
                    <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200 shadow-lg">
                        <div className="flex items-center mb-2">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 animate-pulse"></div>
                            <p className="text-blue-800 font-medium">Chargement en cours...</p>
                        </div>
                        <p className="text-blue-700 text-sm">{debugInfo}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !post) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50 flex items-center justify-center pt-16">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-2xl shadow-2xl p-8">
                        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>

                        <h1 className="text-3xl font-bold text-gray-900 mb-4">404</h1>
                        <p className="text-xl text-gray-600 mb-6">{error || 'Article non trouvé'}</p>

                        {/* Debug info */}
                        {debugInfo && (
                            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-left">
                                <h4 className="font-semibold text-yellow-800 mb-2">Informations de debug :</h4>
                                <p className="text-yellow-700 text-sm">{debugInfo}</p>
                                <p className="text-yellow-700 text-sm mt-2">
                                    <strong>Slug recherché :</strong> {slug}
                                </p>
                            </div>
                        )}

                        <div className="space-y-4">
                            <Link
                                to="/actualites"
                                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Retour aux actualités
                            </Link>

                            <div className="text-sm text-gray-500 pt-4 border-t border-gray-200">
                                <p className="mb-2">Ou essayez d'accéder à l'article directement :</p>
                                <a
                                    href={`https://gmnbckend.gene6.ga/${slug}/`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline font-medium"
                                >
                                    Voir sur WordPress →
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const plainTextContent = wordpressApi.stripHtml(post.content.rendered);
    const excerpt = plainTextContent.substring(0, 155) + '...';
    const cleanTitle = wordpressApi.stripHtml(post.title.rendered);
    const currentUrl = window.location.href;

    // Image par défaut si pas d'image à la une
    const shareImage = featuredImage || '/logo.png';
    // Assurez-vous que l'URL de l'image est absolue
    const absoluteImageUrl = shareImage.startsWith('http')
        ? shareImage
        : `${window.location.origin}${shareImage}`;

    return (
        <>
            {/* SEO Meta tags optimisées pour le partage */}
            <Helmet>
                <title>{cleanTitle} | UDB Actualités</title>
                <meta name="description" content={excerpt} />

                {/* Open Graph (Facebook, WhatsApp, LinkedIn) */}
                <meta property="og:title" content={cleanTitle} />
                <meta property="og:description" content={excerpt} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={currentUrl} />
                <meta property="og:image" content={absoluteImageUrl} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={cleanTitle} />
                <meta property="og:site_name" content="UDB Actualités" />
                <meta property="og:locale" content="fr_FR" />
                <meta property="article:published_time" content={post.date} />
                <meta property="article:modified_time" content={post.modified} />
                <meta property="article:author" content="Union Démocratique des Bâtisseurs" />
                <meta property="article:section" content="Actualités" />

                {/* Twitter Cards */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={cleanTitle} />
                <meta name="twitter:description" content={excerpt} />
                <meta name="twitter:image" content={absoluteImageUrl} />
                <meta name="twitter:image:alt" content={cleanTitle} />
                <meta name="twitter:site" content="@UDB_Officiel" />

                {/* WhatsApp spécifique */}
                <meta property="whatsapp:title" content={cleanTitle} />
                <meta property="whatsapp:description" content={excerpt} />
                <meta property="whatsapp:image" content={absoluteImageUrl} />

                {/* Telegram */}
                <meta property="telegram:title" content={cleanTitle} />
                <meta property="telegram:description" content={excerpt} />
                <meta property="telegram:image" content={absoluteImageUrl} />

                {/* Schema.org structured data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "NewsArticle",
                        "headline": cleanTitle,
                        "description": excerpt,
                        "image": {
                            "@type": "ImageObject",
                            "url": absoluteImageUrl,
                            "width": 1200,
                            "height": 630
                        },
                        "datePublished": post.date,
                        "dateModified": post.modified,
                        "author": {
                            "@type": "Organization",
                            "name": "Union Démocratique des Bâtisseurs"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "UDB Actualités",
                            "logo": {
                                "@type": "ImageObject",
                                "url": `${window.location.origin}/logo.png`,
                                "width": 200,
                                "height": 200
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": currentUrl
                        }
                    })}
                </script>
            </Helmet>

            <br/><br/><br/>

            <article className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 pt-16">
                {/* Breadcrumb amélioré */}
                <nav className="bg-white shadow-sm border-b">
                    <div className="max-w-4xl mx-auto px-4 py-4">
                        <ol className="flex items-center space-x-3 text-sm">
                            <li>
                                <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors font-medium">
                                    Accueil
                                </Link>
                            </li>
                            <li className="text-gray-400">/</li>
                            <li>
                                <Link to="/actualites" className="text-blue-600 hover:text-blue-800 transition-colors font-medium">
                                    Actualités
                                </Link>
                            </li>
                            <li className="text-gray-400">/</li>
                            <li className="text-gray-900 font-medium truncate">
                                {cleanTitle}
                            </li>
                        </ol>
                    </div>
                </nav>

                {/* Contenu principal dans un container amélioré */}
                <div className="max-w-4xl mx-auto px-4 py-12">
                    {/* En-tête de l'article amélioré */}
                    <header className="text-center mb-12">
                        <h1
                            className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-8 leading-tight"
                            dangerouslySetInnerHTML={createMarkup(post.title.rendered)}
                        />

                        {/* Meta informations avec style amélioré */}
                        <div className="flex flex-wrap items-center justify-center gap-6 text-gray-600 mb-8">
                            <time className="flex items-center bg-blue-50 px-4 py-2 rounded-full">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {wordpressApi.formatDate(post.date)}
                            </time>

                            <span className="flex items-center bg-green-50 px-4 py-2 rounded-full">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                </svg>
                                {wordpressApi.getReadingTime(post.content.rendered)} min de lecture
                            </span>
                        </div>
                    </header>

                    {/* Image à la une avec style amélioré */}
                    {featuredImage && (
                        <div className="mb-12">
                            <img
                                src={featuredImage}
                                alt={cleanTitle}
                                className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-2xl"
                                loading="eager"
                            />
                        </div>
                    )}

                    {/* Contenu principal dans une boîte stylisée */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-12">
                        <div
                            className="wordpress-content"
                            dangerouslySetInnerHTML={createMarkup(post.content.rendered)}
                        />
                    </div>

                    {/* Boutons de partage améliorés */}
                    <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
                        <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">Partager cet article</h3>
                        <div className="flex flex-wrap justify-center items-center gap-4">
                            {/* WhatsApp */}
                            <button
                                onClick={() => {
                                    const text = encodeURIComponent(`${cleanTitle}\n\n${excerpt}\n\nLire plus: ${currentUrl}`);
                                    window.open(`https://wa.me/?text=${text}`, '_blank');
                                }}
                                className="flex items-center px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.520-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.485 3.488"/>
                                </svg>
                                WhatsApp
                            </button>

                            {/* Telegram */}
                            <button
                                onClick={() => {
                                    const text = encodeURIComponent(`${cleanTitle}\n\n${excerpt}\n\nLire plus: ${currentUrl}`);
                                    window.open(`https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${text}`, '_blank');
                                }}
                                className="flex items-center px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                                </svg>
                                Telegram
                            </button>

                            {/* Twitter */}
                            <button
                                onClick={() => {
                                    const text = encodeURIComponent(`${cleanTitle}\n\n${excerpt.substring(0, 100)}...`);
                                    const url = encodeURIComponent(currentUrl);
                                    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
                                }}
                                className="flex items-center px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                </svg>
                                Twitter
                            </button>

                            {/* Facebook */}
                            <button
                                onClick={() => {
                                    const url = encodeURIComponent(currentUrl);
                                    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
                                }}
                                className="flex items-center px-6 py-3 bg-blue-800 text-white rounded-xl hover:bg-blue-900 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                                Facebook
                            </button>

                            {/* LinkedIn */}
                            <button
                                onClick={() => {
                                    const url = encodeURIComponent(currentUrl);
                                    const title = encodeURIComponent(cleanTitle);
                                    const summary = encodeURIComponent(excerpt);
                                    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}&title=${title}&summary=${summary}`, '_blank');
                                }}
                                className="flex items-center px-6 py-3 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                                </svg>
                                LinkedIn
                            </button>

                            {/* Copier le lien */}
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(currentUrl);
                                    showToast('Lien copié dans le presse-papiers !');
                                }}
                                className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition-all transform hover:scale-105 shadow-lg"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                Copier le lien
                            </button>
                        </div>
                    </div>
                </div>

                {/* Articles liés améliorés */}
                {relatedPosts.length > 0 && (
                    <section className="bg-white py-16">
                        <div className="max-w-4xl mx-auto px-4">
                            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Articles liés</h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {relatedPosts.map((relatedPost) => (
                                    <Link
                                        key={relatedPost.id}
                                        to={`/actualites/${relatedPost.slug}`}
                                        className="group block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all transform hover:scale-105"
                                    >
                                        <div className="h-48 bg-gradient-to-br from-blue-500 to-blue-700 relative overflow-hidden">
                                            {relatedPost.featured_media ? (
                                                <RelatedPostImage
                                                    mediaId={relatedPost.featured_media}
                                                    title={wordpressApi.stripHtml(relatedPost.title.rendered)}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center">
                                                    <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <h3
                                                className="font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors"
                                                dangerouslySetInnerHTML={createMarkup(relatedPost.title.rendered)}
                                            />
                                            <time className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                                {wordpressApi.formatDate(relatedPost.date)}
                                            </time>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </section>
                )}

                {/* Navigation améliorée */}
                <nav className="bg-gradient-to-r from-gray-100 to-blue-100 py-12">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <Link
                            to="/actualites"
                            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-all transform hover:scale-105 shadow-xl"
                        >
                            <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Retour aux actualités
                        </Link>
                    </div>
                </nav>
            </article>
        </>
    );
};

export { ArticlePage };
